// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { Item, ItemsFilter, itemSchema } from '@/items/api/types';
import { z } from 'zod';

const itemsApiResponseSchema = z.object({
  results: z.array(itemSchema),
  totalCount: z.number(),
});

/**
 * Type for the raw API response envelope.
 */
interface ApiResponse {
  data: unknown;
}

/**
 * Response from the items API including pagination data.
 */
interface ItemsApiResponse {
  results: Item[];
  totalCount: number;
}

/**
 * Fetches items from the unified Item Catalog API.
 *
 * @param filter - Optional filter parameters (type, search, pagination, sorting)
 * @returns A promise that resolves to the items response with pagination
 */
export async function getItems(
  filter?: ItemsFilter
): Promise<ItemsApiResponse> {
  const url = '/v1/catalog/item';
  // When archived=true, we want to include ALL items (archived + non-archived)
  // So we send 'all' to the backend. When undefined/false, we don't send it
  // and the backend defaults to showing only non-archived items.
  const archivedParam = filter?.archived ? 'all' : undefined;

  const response = await api.get<ApiResponse>(url, {
    params: {
      type: filter?.type === 'all' ? undefined : filter?.type,
      search: filter?.search,
      page: filter?.page,
      limit: filter?.limit,
      sortBy: filter?.sort,
      sortOrder: filter?.direction,
      archived: archivedParam,
      excludeLinkedIntegration: filter?.excludeLinkedIntegration,
    },
  });

  return parseResponse(itemsApiResponseSchema, response.data.data, {
    schemaName: 'Items',
    endpoint: url,
  });
}
